import {
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Input,
  InputNumber,
  Pagination,
  Popconfirm,
  Space,
  Table,
  TableColumnsType,
  Tooltip,
} from "antd";
import { AlertTriangle, Edit, Trash2 } from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNotification } from "~/hooks";
import { useDebounceValue } from "~/hooks/use-debounce";
import {
  SUPPLY_KEYS,
  useDeleteSupply,
  useGetSupplies,
  useUpdateSupply,
} from "~/services/supply";
import { TSupply } from "~/types";
import { cn, pluralize } from "~/utils";
import { SuppliesType } from "~/utils/constants";
import { useSupplyStore } from "./supply.store";

type Props = {};

export const TableSupplies: FC<Props> = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [search] = useDebounceValue(searchTerm, 500);
  const [editingQuantity, setEditingQuantity] = useState<{
    [key: string]: number;
  }>({});

  const { filters, setFilters, openSupplyModal } = useSupplyStore();
  const { data: supplies, isLoading } = useGetSupplies(filters);
  const updateMutation = useUpdateSupply();
  const qc = useQueryClient();
  const { success, error } = useNotification();

  useEffect(() => {
    setFilters({ search });
  }, [search, setFilters]);

  const handleQuantityChange = (id: string, quantity: number) => {
    setEditingQuantity((prev) => ({ ...prev, [id]: quantity }));
  };

  const handleQuantitySave = useCallback(
    (id: string) => {
      if (editingQuantity[id] !== undefined) {
        updateMutation.mutate(
          {
            id,
            currentStock: editingQuantity[id],
          },
          {
            onSuccess: () => {
              success({
                message: "Quantity updated successfully",
              });
              setEditingQuantity((prev) => {
                const newState = { ...prev };
                delete newState[id];
                return newState;
              });
              qc.invalidateQueries({
                queryKey: SUPPLY_KEYS.GET_SUPPLIES(filters),
              });
            },
            onError: (err) => {
              error({
                message: "Error updating quantity",
                description: err?.message || "An error occurred.",
              });
            },
          }
        );
      }
    },
    [editingQuantity, error, filters, qc, success, updateMutation]
  );

  const columns: TableColumnsType<TSupply> = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "supplyRef",
        width: "10%",
        render: (id) => (
          <div className="text-gray-700 font-mono text-xs">{id}</div>
        ),
      },
      {
        title: "Item",
        dataIndex: "name",
        width: "25%",
        render: (_, record) => (
          <div className="flex items-start">
            <div>
              <div className="font-medium">{record.name}</div>
              <div className="text-xs text-gray-500">
                {record.brand && `${record.brand} · `}
                {record.category && `${record.category}`}
              </div>
              {record.suppliesType === SuppliesType.REFURBISHED_CRATES &&
                (record.insideLength || record.insideWidth || record.insideHeight) && (
                  <div className="text-xs text-gray-500 mt-1">
                    Inside: {record.insideLength || 0}" x {record.insideWidth || 0}" x {record.insideHeight || 0}"
                  </div>
                )}
            </div>
          </div>
        ),
      },
      {
        title: "Location",
        dataIndex: ["location", "name"],
        width: "15%",
        render: (name) => <div>{name || "N/A"}</div>,
      },
      {
        title: "Quantity",
        dataIndex: "currentStock",
        width: "15%",
        render: (stock, record) => (
          <div className="flex items-center space-x-2">
            {editingQuantity[record.id] !== undefined ? (
              <>
                <InputNumber
                  min={0}
                  value={editingQuantity[record.id]}
                  onChange={(val) => handleQuantityChange(record.id, val || 0)}
                  onPressEnter={() => handleQuantitySave(record.id)}
                  onBlur={() => handleQuantitySave(record.id)}
                  className="w-20"
                />
                <span>{record.unit ? pluralize(record.unit) : ""}</span>
              </>
            ) : (
              <div
                className={cn(
                  "cursor-pointer hover:bg-gray-100 px-2 py-1 rounded flex items-center",
                  stock <= record.minimumStock ? "text-red-500 font-medium" : ""
                )}
                onClick={() =>
                  setEditingQuantity({ ...editingQuantity, [record.id]: stock })
                }
                title="Click to edit quantity"
              >
                <span>{stock}</span>
                <span className="ml-1">
                  {record.unit ? pluralize(record.unit) : ""}
                </span>
                {stock <= record.minimumStock && (
                  <Tooltip title="Below minimum stock level">
                    <AlertTriangle size={14} className="ml-1 text-red-500" />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Min. Stock",
        dataIndex: "minimumStock",
        width: "10%",
        render: (stock: number, record) => (
          <span>
            {stock} {record.unit ? pluralize(record.unit) : ""}
          </span>
        ),
      },
      {
        title: "Vendor",
        dataIndex: ["vendor", "name"],
        width: "15%",
        render: (name) => name || "N/A",
      },
      {
        title: "Actions",
        width: "10%",
        render: (_, record) => <RowActions record={record} />,
      },
    ],
    [editingQuantity, handleQuantitySave]
  );
  return (
    <Card
      classNames={{
        body: "p-0",
      }}
    >
      <div className="p-4 border-b flex justify-between items-center">
        <h3 className="font-medium">All Supplies</h3>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search supplies..."
          style={{ width: 256 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <Table
        columns={columns}
        dataSource={supplies?.items}
        loading={isLoading}
        rowKey={(row) => row.id}
        pagination={false}
      />
      <div className="p-4 border-t flex justify-between items-center">
        <span className="text-sm text-gray-500">
          Showing {supplies?.items.length ?? 0} of{" "}
          {supplies?.meta.totalItems ?? 0} items
        </span>
        <Pagination
          {...{
            pageSize: filters.pageSize || 10,
            current: filters.page || 1,
            total: supplies?.meta.totalItems || (supplies?.items ?? []).length,
            onChange: (page, pageSize) => {
              (useSupplyStore as any).setState({
                filters: { ...filters, page, pageSize },
              });
            },
            hideOnSinglePage: true,
          }}
        />
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => openSupplyModal()}>
            Add New Item
          </Button>
          <Button icon={<UploadOutlined />}>Export CSV</Button>
        </Space>
      </div>
    </Card>
  );
};

const RowActions: FC<{ record: TSupply }> = ({ record }) => {
  const qc = useQueryClient();

  const { success, error } = useNotification();

  const mutation = useDeleteSupply();

  const [showConfirm, setShowConfirm] = useState(false);

  const { filters, openSupplyModal } = useSupplyStore();

  return (
    <div className="flex space-x-2">
      <Tooltip title="Edit supply">
        <button
          onClick={() => openSupplyModal(record.id)}
          className="text-blue-600 hover:text-blue-800"
        >
          <Edit size={16} />
        </button>
      </Tooltip>
      <Popconfirm
        open={showConfirm}
        title="Are you sure you want to delete this supply?"
        onConfirm={() => {
          mutation.mutate(record.id, {
            onSuccess: () => {
              setShowConfirm(false);
              success({
                message: "Supply deleted successfully",
              });
              qc.invalidateQueries({
                queryKey: SUPPLY_KEYS.GET_SUPPLIES(filters),
              });
            },
            onError: (err) => {
              console.log("error", err);
              setShowConfirm(false);
              error({
                message: "Error deleting supply",
                description: err.message,
              });
            },
          });
        }}
        okButtonProps={{ loading: mutation.isPending }}
        okText="Yes"
        cancelText="No"
      >
        <Tooltip title="Delete supply">
          <Button type="link" danger onClick={() => setShowConfirm(true)}>
            <Trash2 size={16} />
          </Button>
        </Tooltip>
      </Popconfirm>
    </div>
  );
};
